import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Clock, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import { useTrialManagement } from "@/hooks/useTrialManagement";
import { useWorkspace } from "@/hooks/useWorkspace";

export const TrialStatusBanner: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const { trialAnalysis, personalizedOffer, trackConversionEvent } = useTrialManagement();

  if (!currentWorkspace?.trial_expires_at || currentWorkspace?.trial_status === 'converted') {
    return null;
  }

  const daysLeft = Math.ceil(
    (new Date(currentWorkspace.trial_expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  const isExpiringSoon = daysLeft <= 3;
  const trialProgress = trialAnalysis?.engagement_score || 0;

  const handleUpgradeClick = () => {
    trackConversionEvent.mutate({
      event_type: 'upgrade_click',
      event_data: { source: 'trial_banner' }
    });
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5 p-6 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              {daysLeft > 0 ? `${daysLeft} days left` : 'Trial expired'}
            </h3>
            {trialAnalysis && (
              <Badge 
                variant="secondary" 
                className={`text-white ${getRiskColor(trialAnalysis.risk_level)}`}
              >
                {trialAnalysis.risk_level.toUpperCase()} RISK
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-6">
            <div className="flex-1 max-w-xs">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Trial Progress: {Math.round(trialProgress)}%
                </span>
              </div>
              <Progress value={trialProgress} className="h-2" />
            </div>

            {trialAnalysis && (
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {Math.round(trialAnalysis.conversion_probability * 100)}%
                </div>
                <div className="text-xs text-muted-foreground">Conversion Likely</div>
              </div>
            )}
          </div>

          {personalizedOffer && (
            <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm text-foreground">
                  {personalizedOffer.urgency_message}
                </span>
              </div>
              {personalizedOffer.bonus_features && (
                <div className="text-xs text-muted-foreground">
                  Includes: {personalizedOffer.bonus_features.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 ml-6">
          <Button
            onClick={handleUpgradeClick}
            className={`px-6 ${isExpiringSoon ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary/90'}`}
          >
            {personalizedOffer?.cta_text || 'Upgrade Now'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          
          {personalizedOffer?.discount_percentage && (
            <Badge variant="secondary" className="text-center bg-green-500 text-white">
              Save {personalizedOffer.discount_percentage}%
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
};